import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Capacitor, registerPlugin } from "@capacitor/core";
import api, { shareOrigin } from "../lib/api";
import BottomNav from "../components/BottomNav";
import ContactLink from "../components/ContactLink";
import IdentityBadge from "../components/IdentityBadge";
import { toast } from "sonner";
import { UserPlus, Mail, Check, X, Trash2, Link2, Copy, Share2, Heart, ChevronRight, BookUser } from "lucide-react";

const NativeShare = registerPlugin("Share");

async function shareInvite(payload) {
  // The optional Capacitor Share plugin opens Android's chooser (Messages,
  // WhatsApp, Messenger, Mail, and every compatible installed app). If the
  // native plugin is not present in an older build, fall through safely to
  // the platform Web Share API.
  if (Capacitor.isNativePlatform()) {
    try {
      await NativeShare.share({ ...payload, dialogTitle: "Invite someone to Proximity" });
      return "shared";
    } catch (error) {
      if (error?.name === "AbortError" || /cancel/i.test(error?.message || "")) return "cancelled";
    }
  }
  if (navigator.share) {
    try {
      await navigator.share(payload);
      return "shared";
    } catch (error) {
      if (error?.name === "AbortError") return "cancelled";
    }
  }
  return "unavailable";
}

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [email, setEmail] = useState("");
  const [relation, setRelation] = useState("friend");
  const [shareLink, setShareLink] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // {user_id, name}

  const load = async () => {
    const [c, ii, oo] = await Promise.all([
      api.get("/contacts"),
      api.get("/contacts/invites/incoming"),
      api.get("/contacts/invites/outgoing"),
    ]);
    setContacts(c.data || []);
    setIncoming(ii.data || []);
    setOutgoing(oo.data || []);
  };
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const [c, ii, oo] = await Promise.all([
          api.get("/contacts"),
          api.get("/contacts/invites/incoming"),
          api.get("/contacts/invites/outgoing"),
        ]);
        if (!active) return;
        setContacts(c.data || []);
        setIncoming(ii.data || []);
        setOutgoing(oo.data || []);
      } catch {
        // Keep the last known contact list visible while offline.
      }
    };
    refresh();
    const timer = window.setInterval(refresh, 5000);
    window.addEventListener("focus", refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      const r = await api.post("/contacts/invite", { email: email.trim().toLowerCase(), relation });
      // The link always uses the public share domain, never the Capacitor
      // WebView's localhost origin. It can be copied/shared if email delivery
      // is not available on the recipient's device.
      setShareLink(`${shareOrigin()}/join/${r.data.token}`);
      toast.success(`Invite link ready for ${email}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite");
    }
  };

  const accept = async (id) => {
    try {
      await api.post(`/contacts/invites/${id}/accept`);
      toast.success("Connected");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't accept invite");
    }
  };
  const decline = async (id) => { await api.post(`/contacts/invites/${id}/decline`); load(); };
  const remove = async (uid) => {
    try {
      await api.delete(`/contacts/${uid}`);
      toast.message("Removed");
      setConfirmRemove(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openOnMap = (c) => navigate(`/dashboard?focus=${encodeURIComponent(c.user_id)}`);

  const toggleFamily = async (c) => {
    const isFam = c.relation === "family" || c.relation === "child" || c.relation === "parent";
    if (c.relation === "child" || c.relation === "parent") {
      toast.message(`${c.name} is already linked as ${c.relation}`);
      return;
    }
    try {
      await api.post(`/contacts/${c.user_id}/family`, { is_family: !isFam });
      toast.success(isFam ? `Moved to friends` : `${c.name} added to family`);
      load();
    } catch { toast.error("Failed"); }
  };

  const generateLink = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/contacts/invite", { relation });
      const url = `${shareOrigin()}/join/${r.data.token}`;
      setShareLink(url);
      load();
      return url;
    } catch (err) {
      // Surface the actual reason so the user isn't stuck at a vague "invite not sent".
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Network error — check your connection and try again";
      toast.error(`Couldn't create invite link: ${msg}`);
      return null;
    } finally { setGenerating(false); }
  };

  const shareWithDeviceContacts = async () => {
    const url = shareLink || (await generateLink());
    if (!url) return; // toast already shown by generateLink
    const text = "Let's share our locations on Proximity — I'll know when you're nearby. ";
    const shareResult = await shareInvite({ title: "Join me on Proximity", text, url });
    if (shareResult === "shared") { toast.success("Invite shared"); return; }
    if (shareResult === "cancelled") return;
    // Fallback: open mail + sms options
    try {
      window.open(`sms:?&body=${encodeURIComponent(text + url)}`, "_self");
      toast.success("Opening messages…");
    } catch {
      // Copy link as last resort
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Invite link copied — paste it in any app");
      } catch {
        toast.error("Sharing not available. Long-press the link to copy it.");
      }
    }
  };

  const pickAndShare = async () => {
    if (!("contacts" in navigator) || !navigator.contacts.select) {
      shareWithDeviceContacts();
      return;
    }
    try {
      const picked = await navigator.contacts.select(["name", "tel", "email"], { multiple: true });
      if (!picked || !picked.length) return;
      const url = shareLink || (await generateLink());
      if (!url) return;
      const text = "Let's share our locations on Proximity. Join me: " + url;
      // Open SMS pre-filled with selected phone numbers if any
      const phones = picked.flatMap((p) => p.tel || []).slice(0, 20);
      if (phones.length) {
        window.open(`sms:${phones.join(",")}?&body=${encodeURIComponent(text)}`, "_self");
      } else {
        shareWithDeviceContacts();
      }
    } catch { /* cancelled or unsupported */ shareWithDeviceContacts(); }
  };

  const copyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const nativeShare = async () => {
    if (!shareLink) return;
    const result = await shareInvite({
      title: "Join me on Proximity",
      text: "Let's share our locations — I'll know when you're nearby.",
      url: shareLink,
    });
    if (result === "unavailable") copyLink();
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-28">
      <header className="px-6 pt-10 pb-6">
        <div className="label-eyebrow">Your circle</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Trusted contacts</h1>
      </header>

      <section className="px-6">
        <div className="rounded-2xl p-5 bg-gradient-to-br from-[#007AFF]/15 via-[#007AFF]/8 to-transparent border border-[#007AFF]/30">
          <div className="label-eyebrow mb-2 text-[#007AFF]">Grow your circle</div>
          <h2 className="font-display font-black text-2xl tracking-tighter">Share Proximity with friends & family</h2>
          <p className="text-sm text-zinc-400 mt-2">
            Send via Messages, WhatsApp, Mail, Telegram — or any app on your phone. They tap the link, sign in, you tap Confirm. Done.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select
              data-testid="share-relation"
              value={relation}
              onChange={(e) => { setRelation(e.target.value); setShareLink(null); }}
              className="bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-sm"
            >
              <option value="friend">As a friend</option>
              <option value="family">As family</option>
              <option value="child">As my child</option>
            </select>
            <button
              data-testid="share-device-btn"
              onClick={shareWithDeviceContacts}
              className="flex-1 min-w-[160px] bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold px-5 py-3 rounded-full inline-flex items-center justify-center gap-2"
            >
              <Share2 size={16}/> Share via apps
            </button>
            {("contacts" in navigator && navigator.contacts?.select) && (
              <button
                data-testid="pick-contact-btn"
                onClick={pickAndShare}
                className="bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold px-4 py-3 rounded-full inline-flex items-center gap-2"
              >
                <UserPlus size={14}/> Pick contact
              </button>
            )}
          </div>
          {shareLink && (
            <div className="mt-3 flex items-center gap-2">
              <input
                data-testid="invite-link-value"
                readOnly value={shareLink}
                onFocus={(e)=>e.target.select()}
                className="flex-1 bg-zinc-950/60 border border-white/10 rounded-lg px-3 py-2 text-[11px] font-mono truncate"
              />
              <button
                data-testid="copy-link-btn"
                type="button" onClick={copyLink}
                className="size-9 rounded-lg border border-white/10 grid place-items-center hover:bg-white/5"
                title="Copy"
              >
                <Copy size={14}/>
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="px-6 mt-6">
        <Link
          to="/phonebook"
          data-testid="open-phonebook"
          className="block p-4 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-[#34C759]/15 grid place-items-center">
              <BookUser size={18} className="text-[#34C759]"/>
            </div>
            <div className="flex-1">
              <div className="font-display font-bold">Phone Contacts</div>
              <div className="text-xs text-zinc-500">See which of your phone contacts are on Proximity — invite the rest in one tap.</div>
            </div>
            <ChevronRight size={16} className="text-zinc-500"/>
          </div>
        </Link>
      </section>

      <section className="px-6 mt-6">
        <details className="rounded-2xl border border-white/5 bg-white/[0.02] open:bg-white/[0.04]">
          <summary className="cursor-pointer p-4 list-none flex items-center gap-2 text-sm font-semibold">
            <Mail size={14} className="text-zinc-500"/> Invite by email instead
            <span className="ml-auto text-zinc-500 text-xs">▾</span>
          </summary>
          <form onSubmit={invite} data-testid="invite-form" className="p-4 pt-0">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
                <input
                  data-testid="invite-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  placeholder="friend@example.com"
                  className="w-full bg-zinc-900 border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#007AFF]"
                />
              </div>
              <button
                data-testid="send-invite-btn"
                type="submit"
                className="bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold px-5 py-2.5 rounded-lg"
              >
                Send
              </button>
            </div>
          </form>
        </details>
      </section>

      {incoming.length > 0 && (
        <section className="px-6 mt-6">
          <div className="label-eyebrow mb-3">Pending requests</div>
          <div className="space-y-2">
            {incoming.map((i) => (
              <div key={i.invite_id} className="flex items-center gap-3 p-4 rounded-xl border border-[#FFCC00]/20 bg-[#FFCC00]/5">
                <div className="size-10 rounded-full bg-zinc-800 grid place-items-center font-display font-bold">{(i.owner_name||"?").slice(0,1)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{i.owner_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{i.owner_email} · {i.relation}</div>
                </div>
                <button data-testid={`accept-${i.invite_id}`} onClick={() => accept(i.invite_id)} className="size-9 rounded-full bg-[#34C759] grid place-items-center text-white"><Check size={16}/></button>
                <button onClick={() => decline(i.invite_id)} className="size-9 rounded-full bg-white/5 border border-white/10 grid place-items-center"><X size={16}/></button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="px-6 mt-6">
        <div className="label-eyebrow mb-3">Connected · {contacts.length}</div>
        {contacts.length === 0 && (
          <div className="p-8 rounded-2xl border border-white/5 text-center text-zinc-500">
            No contacts yet. Send an invite to get started.
          </div>
        )}
        <div className="space-y-2">
          {contacts.map((c) => (
            <div key={c.user_id} className="flex items-center gap-3 p-4 rounded-xl border border-white/5 hover:bg-white/[0.03]">
              <button
                data-testid={`contact-open-${c.user_id}`}
                onClick={() => openOnMap(c)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
              >
                <div className="size-11 rounded-full bg-zinc-800 overflow-hidden grid place-items-center font-display font-bold">
                  {c.picture ? <img src={c.picture} alt="" className="w-full h-full object-cover" /> : (c.name||"?").slice(0,1)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-1.5">
                    <IdentityBadge contact={c} size={18} />
                    {c.display_name || c.name}
                    <ChevronRight size={12} className="text-zinc-500"/>
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{c.email} · {c.relation}</div>
                </div>
              </button>
              <div className="font-mono text-sm">
                {c.distance_miles == null ? "—" :
                  c.distance_miles < 0.19 ? `${Math.round(c.distance_miles*5280)} ft`
                  : `${c.distance_miles.toFixed(2)} mi`}
              </div>
              <button
                data-testid={`family-toggle-${c.user_id}`}
                onClick={() => toggleFamily(c)}
                className={`size-9 rounded-full border grid place-items-center ${
                  (c.relation === "family" || c.relation === "child" || c.relation === "parent")
                    ? "border-[#FF2D55] text-[#FF2D55] bg-[#FF2D55]/10"
                    : "border-white/10 text-zinc-400 hover:text-[#FF2D55] hover:border-[#FF2D55]/40"
                }`}
                title={c.relation === "family" ? "Remove from family" : "Add to family"}
              >
                <Heart size={15} fill={(c.relation === "family" || c.relation === "child" || c.relation === "parent") ? "#FF2D55" : "none"}/>
              </button>
              <button
                data-testid={`remove-${c.user_id}`}
                onClick={() => setConfirmRemove({ user_id: c.user_id, name: c.name })}
                className="size-9 rounded-full border border-white/10 grid place-items-center text-zinc-400 hover:text-[#FF3B30] hover:border-[#FF3B30]/40"
                title="Remove contact"
              >
                <Trash2 size={15}/>
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Delete confirmation modal */}
      {confirmRemove && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setConfirmRemove(null)}>
          <div
            data-testid="remove-contact-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-3xl border border-white/10 bg-zinc-950 p-6"
          >
            <h2 className="font-display font-black text-2xl tracking-tighter">Remove contact?</h2>
            <p className="text-sm text-zinc-400 mt-2">
              Are you sure you want to remove <span className="font-semibold text-zinc-100">{confirmRemove.name}</span>? You'll disappear from each other's maps. You can reconnect later by invite.
            </p>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setConfirmRemove(null)}
                className="flex-1 py-2.5 rounded-full border border-white/10 font-bold"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-remove-contact"
                onClick={() => remove(confirmRemove.user_id)}
                className="flex-1 py-2.5 rounded-full bg-[#FF3B30] text-white font-bold"
              >
                Remove contact
              </button>
            </div>
          </div>
        </div>
      )}

      {outgoing.length > 0 && (
        <section className="px-6 mt-6">
          <div className="label-eyebrow mb-3">Awaiting reply</div>
          <div className="space-y-2">
            {outgoing.map((i) => (
              <div key={i.invite_id} className="flex items-center gap-3 p-4 rounded-xl border border-white/5">
                <Mail size={16} className="text-zinc-500"/>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{i.target_email}</div>
                  <div className="text-xs text-zinc-500">Sent · {i.relation}</div>
                </div>
                <span className="font-mono text-xs text-[#FFCC00]">PENDING</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <BottomNav />
    </div>
  );
}
